
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import External from 'plaid-threads/Icons/External';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  description: 'Instantly retrieve account information to set up pay-by-bank payments via ACH and more'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const APIVersion = makeShortcode("APIVersion");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Auth'}>{metadata.description}</Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Auth'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/auth/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='/docs/quickstart' />
  <Actions.SmallCard icon={<External mdxType="External" />} title='Demo' link='https://plaid.coastdemo.com/share/6786ccc5a048a5f1cf748cb5?zoom=100' /> 
    </Actions.SmallWrapper>
    <p>{`Auth allows you to request a user's checking, savings, or cash management account and routing number, making it easy for you to initiate credits or debits via ACH, wire transfer, or equivalent international networks. Auth makes it easy for end users to fund an account with your app, cash out to their bank account, or make purchases via pay-by-bank.`}</p>
    <p>{`To move money, Auth must be used with a payment processor: either a `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships"
      }}>{`Plaid Partner`}</a>{` or another third party. For more information, see `}<a parentName="p" {...{
        "href": "#using-a-payments-service"
      }}>{`Using a Payments Service`}</a>{`. For an all-in-one solution that includes payment processor functionality, see `}<a parentName="p" {...{
        "href": "/docs/transfer"
      }}>{`Transfer`}</a>{` (US-only). For more details, see `}<a parentName="p" {...{
        "href": "/docs/payments/#auth-and-transfer-comparison"
      }}>{`Auth vs. Transfer comparison`}</a>{`.`}</p>
    <p>{`Auth can only be used with debitable checking, savings, or cash management accounts. Credit-type accounts, including credit cards, cannot receive payments directly via electronic interbank transfers, and Auth data cannot be used to set up credit card payments. Auth can not be used to connect debit cards; instead, you can make a transfer directly from the user's bank account, saving you money over using the debit card network.`}</p>
    <YouTubeEmbed videoId='2ZsANOjVjIs' alignment='full' title='Plaid Auth in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Auth works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <p>{`Auth is commonly used in combination with other Plaid APIs that reduce risk and support compliance, such as `}<a parentName="p" {...{
        "href": "/docs/balance"
      }}>{`Balance`}</a>{` (to verify accounts have sufficient funds), `}<a parentName="p" {...{
        "href": "/docs/signal"
      }}>{`Signal`}</a>{` (to calculate the risk of ACH returns with ML-powered analysis), and `}<a parentName="p" {...{
        "href": "/docs/identity"
      }}>{`Identity`}</a>{` (to verify ownership information on the account).`}</p>
    <p>{`For account funding use cases, see `}<a parentName="p" {...{
        "href": "/docs/identity-verification"
      }}>{`Identity Verification`}</a>{` for an end-to-end KYC compliance solution with optional AML capabilities.`}</p>
    <h4 {...{
      "id": "integration-process"
    }}>{`Integration process`}</h4>
    <p>{`Below is a high level overview of the Auth integration process. For a more detailed walkthrough, see `}<a parentName="p" {...{
        "href": "/docs/auth/add-to-app"
      }}>{`Add auth to your app`}</a>{` or (if applicable) the docs for the specific `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships"
      }}>{`partner`}</a>{` you are using.`}</p>
  <Callout title="Embedded Link for pay-by-bank applications" mdxType="Callout">
      <p>{`If your use case involves the end user choosing between paying via a credit card and paying via a bank account, it is strongly recommended to use the `}<a parentName="p" {...{
          "href": "/docs/link/embedded-institution-search/"
        }}>{`Embedded experience`}</a>{` for Link to increase uptake of pay-by-bank.`}</p>
    </Callout>
    <ol>
      <li parentName="ol">{`Create a Link token by calling `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` with `}<inlineCode parentName="li">{`auth`}</inlineCode>{` in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` parameter. `}</li>
      <li parentName="ol">{`Initialize Link with the Link token from the previous step. For more details, see `}<a parentName="li" {...{
          "href": "/docs/link"
        }}>{`Link`}</a>{`. `}
        <ul parentName="li">
          <li parentName="ul">{`For Link configuration recommendations, see `}<a parentName="li" {...{
              "href": "#optimizing-the-link-ui-for-auth"
            }}>{`Optimizing the Link UI for Auth`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`Once the user has successfully completed the Link flow, exchange the `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` by calling `}<inlineCode parentName="li">{`/item/public_token/exchange`}</inlineCode>{`. `}</li>
      <li parentName="ol">{`If using a Plaid partner for payment processing, ensure the partner is enabled on your `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/developers/integrations"
        }}>{`Plaid Dashboard`}</a>{`, then call `}<inlineCode parentName="li">{`/processor/token/create`}</inlineCode>{` or `}<inlineCode parentName="li">{`/processor/stripe/bank_account_token/create`}</inlineCode>{` to obtain a token that you will provide to the partner to enable funds transfers. For more detailed instructions, including a full walkthrough, see `}<a parentName="li" {...{
          "href": "/docs/auth/partnerships/"
        }}>{`Auth payment partners`}</a>{`. `}</li>
      <li parentName="ol">{`If not using a Plaid partner, call `}<inlineCode parentName="li">{`/auth/get`}</inlineCode>{` to obtain the account and routing number, then provide these fields to your payment processing system.`}</li>
      <li parentName="ol">{`Listen for the `}<a parentName="li" {...{
          "href": "/docs/api/items/#pending_disconnect"
        }}><inlineCode parentName="a">{`PENDING_DISCONNECT`}</inlineCode></a>{` webhook. If you receive it, send the Item through `}<a parentName="li" {...{
          "href": "/docs/link/update-mode"
        }}>{`update mode`}</a>{`. If update mode is not completed for the Item within 7 days of webhook receipt, consent will expire, which may lead to the account and routing number becoming invalid. For more details, see `}<a parentName="li" {...{
          "href": "#tan-expiration"
        }}>{`TAN expiration`}</a>{`. When consent expires, the Item enters the `}<inlineCode parentName="li">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` state and Plaid sends an `}<a parentName="li" {...{
          "href": "/docs/api/items/#error"
        }}><inlineCode parentName="a">{`ITEM: ERROR`}</inlineCode></a>{` webhook; there is no separate expiration webhook. `}</li>
      <li parentName="ol">{`Listen for the `}<a parentName="li" {...{
          "href": "/docs/api/items/#user_permission_revoked"
        }}><inlineCode parentName="a">{`USER_PERMISSION_REVOKED`}</inlineCode></a>{` and `}<a parentName="li" {...{
          "href": "/docs/api/items/#user_account_revoked"
        }}><inlineCode parentName="a">{`USER_ACCOUNT_REVOKED`}</inlineCode></a>{` webhooks. These webhooks indicate that you should send the Item through `}<a parentName="li" {...{
          "href": "/docs/link/update-mode"
        }}>{`update mode`}</a>{` to refresh consent; if update mode cannot be launched for the Item, create a new Item instead. Attempting to use the account and routing number for transactions without successfully completing update mode may result in ACH returns. For more details, see `}<a parentName="li" {...{
          "href": "#tan-expiration"
        }}>{`TAN expiration`}</a>{`. `}</li>
      <li parentName="ol">{`Implement special remediation steps for Chase Items: if consent has expired as described in step 6, or if the permissions have been revoked as described in step 7, then the account and routing number will become invalid and must be regenerated. In addition to sending the Item through update mode, you must `}<em parentName="li">{`also`}</em>{` call `}<inlineCode parentName="li">{`/auth/get`}</inlineCode>{`, `}<inlineCode parentName="li">{`/processor/token/create`}</inlineCode>{`, or `}<inlineCode parentName="li">{`/processor/stripe/bank_account_token/create`}</inlineCode>{`, as relevant to your use case, to get a new set of routing and account numbers. To simplify your application logic, you can do this every time you send a Chase Item through update mode when using Auth. For more details, see `}<a parentName="li" {...{
          "href": "#tan-expiration"
        }}>{`TAN expiration`}</a>{`. `}</li>
      <li parentName="ol">{`(Optional) Listen for the `}<a parentName="li" {...{
          "href": "/docs/api/products/auth/#default_update"
        }}><inlineCode parentName="a">{`AUTH: DEFAULT_UPDATE`}</inlineCode></a>{` webhook. This webhook indicates that the routing and/or account number for an account on the Item has changed (this is very rare). Call `}<inlineCode parentName="li">{`/auth/get`}</inlineCode>{`, `}<inlineCode parentName="li">{`/processor/token/create`}</inlineCode>{`, or `}<inlineCode parentName="li">{`/processor/stripe/bank_account_token/create`}</inlineCode>{` on that Item, as applicable for your integration, and use only the new numbers or processor token.`}</li>
      <li parentName="ol">{`(Optional) To enable and configure additional Auth verification flows, such as micro-deposit verification or database verification, use the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/account-verification"
        }}>{`Account Verification Dashboard`}</a>{`. For demos and more information, see `}<a parentName="li" {...{
          "href": "/docs/auth/coverage/"
        }}>{`Additional verification flows`}</a>{`.`}</li>
    </ol>
    <h4 {...{
      "id": "using-a-payments-service"
    }}>{`Using a payments service`}</h4>
    <Callout mdxType="Callout">
      <p>{`Looking for bank-to-bank transfer capabilities and don't have a payment processor yet? Check out `}<a parentName="p" {...{
          "href": "/docs/transfer"
        }}>{`Transfer`}</a>{` (US only) for a money movement solution with built-in payment processing capabilities. See `}<a parentName="p" {...{
          "href": "/docs/payments#auth-and-transfer-comparison"
        }}>{`Auth and Transfer comparison`}</a>{` to learn more.`}</p>
    </Callout>
    <p>{`When using Auth, you will send Auth data to a payments service to initiate an interbank transfer; Plaid does not function as the payment processor. While Plaid is processor-agnostic and allows you to work with any partner you want, one easy way to make transfers is to work with a payments platform that partners with Plaid, such as Dwolla or Square. Working with these partners, you will not call the `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` endpoint, so you will not obtain a user's bank account information. Instead, you will call `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{` or `}<inlineCode parentName="p">{`/processor/stripe/bank_account_token/create`}</inlineCode>{` to obtain a Plaid token that you will provide to the partner and that allows them to make these Plaid API calls as needed. For detailed instructions on how to set up Auth with a Plaid partner, as well as a list of supported funds transfer partners, see `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships"
      }}>{`Auth Partnerships`}</a>{`.`}</p>
    <p>{`You are free to mix-and-match usage of a processor partner with your own direct usage of the Plaid API on the same linked Items. For example, if you have created a `}<inlineCode parentName="p">{`processor_token`}</inlineCode>{` for a partner to use, you may choose to have this partner also perform ACH return risk assessment using the processor token. But if the partner does not provide this functionality, or you wish to do it yourself, you may do your own risk assessment by calling `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` directly using the Item's access token.`}</p>
    <p>{`If you choose to use a payments provider who is not a Plaid partner, you will need to obtain bank account numbers and codes directly using `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`. In the US, this data must be stored and handled in compliance with `}<a parentName="p" {...{
        "href": "https://www.nacha.org/"
      }}>{`Nacha rules`}</a>{`; consult your internal compliance team or the Nacha site for details. Given the sensitive nature of this information, we also recommend that you consult the `}<a parentName="p" {...{
        "href": "https://security.plaid.com/documents?id=8967b9c3-1e20-407d-8549-f5e0b29786e7&search=ofdss"
      }}>{`Open Finance Data Security Standard`}</a>{` for security best practices. `}</p>
    <p>{`You can also integrate with one of Plaid's `}<a parentName="p" {...{
        "href": "https://plaid.com/partner-directory/"
      }}>{`Data Security partners`}</a>{` to process and share tokenized bank account numbers instead of raw bank account numbers. Contact your account manager to learn more about these partnerships.`}</p>
    <h4 {...{
      "id": "enabling-additional-verification-methods-with-the-account-verification-dashboard"
    }}>{`Enabling additional verification methods with the Account Verification Dashboard`}</h4>
    <p>{`Using the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification"
      }}>{`Account Verification Dashboard`}</a>{`, you can customize the Auth experience to increase conversion, enabling features like micro-deposit-based verification and database verification for end users who cannot or do not want to log into their bank accounts. You can also customize when these features are presented to users. The Account Verification Dashboard also allows you to reduce account takeover fraud by incorporating `}<a parentName="p" {...{
        "href": "/docs/identity/#identity-match"
      }}>{`Identity Match`}</a>{` directly into the Link flow.`}</p>
    <p>{`For more information on the verification options you can enable via the Account Verification Dashboard, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage"
      }}>{`Additional verification methods`}</a>{`.`}</p>
    <p>{`You can also use the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification"
      }}>{`Dashboard`}</a>{` to view the status of any completed, Auth-enabled Link session.`}</p>
    <Image src='/assets/img/docs/auth/av_dashboard_status.png' alt="Session status showing Link Session ID with identity match status and auth method in Account Verification Dashboard." expandable='true' mdxType="Image" />
    <p>{`The Dashboard `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification/analytics"
      }}>{`Account Verification analytics page`}</a>{` shows a breakdown of Auth conversion analytics by `}<a parentName="p" {...{
        "href": "/docs/auth/coverage"
      }}>{`connection method`}</a>{`.`}</p>
    <Image src='/assets/img/docs/auth/av_dashboard_analytics.png' alt='image of Account Verification analytics' expandable='true' mdxType="Image" />
    <h4 {...{
      "id": "optimizing-the-link-ui-for-auth"
    }}>{`Optimizing the Link UI for Auth`}</h4>
    <p>{`By default, only checking, savings, and cash management accounts will appear when using Auth, and institutions that do not support these accounts will not appear in the Institution Select pane.`}</p>
    <p>{`The following Link configuration options are commonly used with Auth:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/link/customization/#account-select"
        }}><strong parentName="a">{`Account select`}</strong></a>{`: The "Account Select: Single select" setting configures the Link UI so that the end user may only select a single account. If you are not using this setting, you will need to build your own UI to let your end user select which linked account they want to use to send or receive funds.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/link/embedded-institution-search"
        }}><strong parentName="a">{`Embedded Institution Search`}</strong></a>{`: This presentation mode shows the Link institution search screen embedded directly into your UI, before the end user has interacted with Link. Embedded Institution Search increases end user uptake of pay-by-bank payment methods and is strongly recommended when implementing Auth as part of a pay-by-bank use case where multiple different payment methods are supported.`}</li>
    </ul>
    <h4 {...{
      "id": "pay-by-bank-optimization"
    }}>{`Pay by bank optimization`}</h4>
    <p>{`If you have a pay-by-bank use case, see `}<a parentName="p" {...{
        "href": "/docs/auth/pay-by-bank-ux"
      }}>{`Increasing pay-by-bank adoption`}</a>{` for tips on how to reduce your transaction fees by increasing the uptake of customers choosing to pay by bank.`}</p>
    <h4 {...{
      "id": "tokenized-account-numbers"
    }}>{`Tokenized account numbers`}</h4>
    <p>{`Institutions that interface with Plaid via an OAuth portal may return a tokenized account number (TAN) in the Auth response. A TAN can be utilized by any ACH processor in place of the user's actual account number, and will be reconciled by the issuing bank on settlement.  Each app a user connects to will receive a unique TAN, rather than the user's exact account number. TANs allow either the end-user or the institution itself to more rigorously monitor and even sever a specific app's ability to transfer funds. These account numbers behave differently from regular account numbers in a number of important ways.`}</p>
    <p>{`To determine if a tokenized account number is being used, use the `}<inlineCode parentName="p">{`is_tokenized_account_number`}</inlineCode>{` field in the `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` or `}<inlineCode parentName="p">{`/processor/auth/get`}</inlineCode>{` response. Currently, only Chase, PNC, and US Bank use tokenized account numbers.`}</p>
    <p>{`Only the `}<inlineCode parentName="p">{`numbers.ach[].account`}</inlineCode>{` field can be set as a TAN (excluding brokerage or international specific numbers results). TANs are only valid for the ACH and RTP payment rails - they may not be used for wire transfers or physical checks. TANs may not be supported by your third-party fraud and account verification databases and vendors.`}</p>
    <p>{`Institutions that use TANs do not always expose them to end users; to avoid confusion, in user-facing UIs, always display the account mask rather than the account number, as end users will not recognize the issued TAN. `}</p>
    <p>{`If a user revokes access to their account (such as via the Chase Security center or `}<a parentName="p" {...{
        "href": "https://my.plaid.com/"
      }}>{`my.plaid.com`}</a>{`), the TAN will become invalid and any attempt to make a transfer using that TAN will fail with an R04 return code. To avoid returns, listen for the `}<a parentName="p" {...{
        "href": "/docs/api/items/#user_permission_revoked"
      }}><inlineCode parentName="a">{`USER_PERMISSION_REVOKED`}</inlineCode></a>{` and `}<a parentName="p" {...{
        "href": "/docs/api/items/#user_account_revoked"
      }}><inlineCode parentName="a">{`USER_ACCOUNT_REVOKED`}</inlineCode></a>{` webhooks and do not use an account number, processor token, or Stripe bank account token associated with an Item or account where access has been revoked. Instead, send the end user through `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{` to restore the Item, then call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{`, or `}<inlineCode parentName="p">{`/processor/stripe/bank_account_token/create`}</inlineCode>{`, as relevant to your use case, to obtain the new TAN. The revoked TAN is not restored by update mode. If update mode cannot be launched for the Item, create a new Item instead.`}</p>
    <p>{`At US Bank, TANs do not become invalid, even after Item deletion or revocation.`}</p>
    <p>{`Because account numbers alone cannot reliably be used to uniquely identify TAN-enabled accounts across different Item instances, Plaid provides a `}<a parentName="p" {...{
        "href": "/docs/api/accounts/#accounts-get-response-accounts-persistent-account-id"
      }}><inlineCode parentName="a">{`persistent_account_id`}</inlineCode></a>{` field for this purpose. This field is only available at institutions that use TANs.`}</p>
    <p>{`If a Chase `}<a parentName="p" {...{
        "href": "/docs/link/duplicate-items/"
      }}>{`duplicate Item`}</a>{` is created, the old Item will be invalidated, but the TAN on the new Item will remain the same. The TAN will only change if the user revokes access or the Item's consent expires. `}</p>
    <p>{`If multiple Items in your app correspond to the same account (for example, in the case of a joint bank account with multiple owners), each Item will typically have a different TAN. `}</p>
    <p>{`TANs will always be accompanied by a routing number. The TAN must be used with the routing number returned by the API. If used with a different routing number, even one associated with the same bank, the transfer may fail.  `}</p>
    <p>{`In Sandbox, the Auth product results will have the `}<inlineCode parentName="p">{`is_tokenized_account_number`}</inlineCode>{` boolean set to true if the Chase (`}<inlineCode parentName="p">{`ins_56`}</inlineCode>{`), PNC (`}<inlineCode parentName="p">{`ins_13`}</inlineCode>{`),  US Bank (`}<inlineCode parentName="p">{`ins_127990`}</inlineCode>{`), or the Platypus OAuth Bank (`}<inlineCode parentName="p">{`ins_127287`}</inlineCode>{`) `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{` is set in the `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` request body or the respective FIs are selected from the Sandbox Link pane.`}</p>
    <h5 {...{
      "id": "tan-expiration"
    }}>{`TAN expiration`}</h5>
    <p>{`At institutions that enforce consent expiration, an Item's TAN can expire along with its consent. An expired TAN cannot be used to make ACH transfers, regardless of how you are accessing the account numbers: via `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`, via the Transfer product, or via processor tokens. Attempting to make a transfer using an expired TAN will fail with an R04 return code.`}</p>
    <p>{`You can check an Item's expiration date by calling `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` or `}<inlineCode parentName="p">{`/item/get`}</inlineCode>{` and looking for the `}<a parentName="p" {...{
        "href": "/docs/api/products/auth/#auth-get-response-item-consent-expiration-time"
      }}><inlineCode parentName="a">{`consent_expiration_time`}</inlineCode></a>{` field in the response. Plaid will also send a `}<a parentName="p" {...{
        "href": "/docs/api/items/#pending_disconnect"
      }}><inlineCode parentName="a">{`PENDING_DISCONNECT`}</inlineCode></a>{` webhook one week before consent is set to expire. To renew consent, send the user through `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{`. If update mode cannot be launched for the Item, create a new Item instead.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`If you send the Item through update mode `}<em parentName="p">{`before`}</em>{` consent expires, the existing TAN remains valid and its life is extended; you do not need to fetch a new one.`}</p>
      <p>{`If the Item's consent had already expired when you sent the Item through update mode, a new TAN will be minted, and you must call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{`, or `}<inlineCode parentName="p">{`/processor/stripe/bank_account_token/create`}</inlineCode>{` to obtain it after completing update mode. The old TAN will not be re-activated. `}</p>
      <p>{`In addition, if the user has multiple depository accounts at the institution, then when going through the OAuth portion of the update mode flow, it's possible that they may de-select the account that they originally shared with you and select a different account instead. If this happens, the TAN of the account they originally shared will be invalidated. For this reason, it is recommended to call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{`, or `}<inlineCode parentName="p">{`/processor/stripe/bank_account_token/create`}</inlineCode>{` `}<em parentName="p">{`every`}</em>{` time you send an Item through update mode at a TAN-issuing institution. `}</p>
      <p>{`Note that there is no additional fee for these API calls, since Auth uses a one-time fee billing model; you are charged only once per Item.`}</p>
    </Callout>
    <p>{`At Chase, a user adding a new Item chooses a consent duration of 6 months, 1 year, or "always". There is no default selection, and the user must make a choice in order to complete the flow. The consent prompt appears on new Items only; existing Chase Items default to "always". However, a user can change the duration for any Chase connection, new or existing, in the Chase Security Center, so an existing Item can acquire an expiration date this way.`}</p>
    <p>{`When a Chase Item's consent expires, the Item will break and the TAN will expire along with it, and the Item will enter an `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` error state. Chase began offering the consent duration choice in August 2026, and the shortest duration it offers is six months, so the earliest possible expirations are in February 2027.`}</p>
    <p><inlineCode parentName="p">{`/item/get`}</inlineCode>{` returns `}<inlineCode parentName="p">{`consent_expiration_time`}</inlineCode>{` for Chase Items connected since August 21, 2026, when Chase began offering the consent duration choice. If the user chose "always", the field is `}<inlineCode parentName="p">{`null`}</inlineCode>{`. You can also use the `}<inlineCode parentName="p">{`PENDING_DISCONNECT`}</inlineCode>{` webhook to detect upcoming expirations at Chase.`}</p>
    <p>{`At PNC, TAN expiration was announced for January 2026, but it is on hold indefinitely. PNC Items still expire after one year if consent is not renewed, but the TAN will continue to be valid.`}</p>
    <h4 {...{
      "id": "updates-to-auth-data"
    }}>{`Updates to Auth data`}</h4>
    <p>{`Because routing and account numbers are inherently persistent, Plaid does not check for updates to Auth data for existing Items except in special circumstances, such as a bank changing its routing numbers due to a system change or merger. Plaid will also check for updated Auth data if a new user account is added to an Item via `}<a parentName="p" {...{
        "href": "/docs/link/update-mode/#using-update-mode-to-request-new-accounts"
      }}>{`update mode`}</a>{`.`}</p>
    <h4 {...{
      "id": "enabling-limited-purpose-checking-accounts-for-rent-or-mortgage"
    }}>{`Enabling limited purpose checking accounts for rent or mortgage`}</h4>
    <Callout warning mdxType="Callout">
      <p>{`The instructions below are intended only for customers who receive rent or mortgage payments via Plaid Auth and who have adequate anti-fraud measures in place to allow accepting ACH payments without balance checks. To learn more, see `}<a parentName="p" {...{
          "href": "/docs/auth/coverage/same-day-link-best-practices/"
        }}>{`Anti-fraud best practices`}</a>{`.`}</p>
    </Callout>
    <p>{`A small number of financial institutions in the rent-tech space have limited-purpose checking accounts that can only be used for paying rent or making mortgage payments. These accounts function as a sort of virtualized ACH interface on top of an end user's funding source (usually their primary bank account). Because these accounts do not provide balance data values that correspond to funds actually available for payments, Plaid does not return balance data for this account subtype. `}</p>
    <p>{`By default, limited-purpose checking accounts cannot be added via Link. However, integrations focused on receiving rent and mortgage payments may wish to enable them. If you would like to opt in to showing them in Link, use the `}<inlineCode parentName="p">{`account_filters`}</inlineCode>{` setting in `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` to enable limited purpose checking accounts.  `}</p>
    <Callout warning mdxType="Callout">
      <p>{`If enabling limited purpose checking accounts, ensure that the filter object includes `}<em parentName="p">{`all`}</em>{` account subtypes you would like to support for Auth, not just the limited purpose checking subtype.`}</p>
    </Callout>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Account filters object setting to add limited purpose checking",
        "#Account": true,
        "filters": true,
        "object": true,
        "setting": true,
        "to": true,
        "add": true,
        "limited": true,
        "purpose": true,
        "checking": true
      }}>{`"account_filters": {
    "depository": {
      "account_subtypes": [
        "checking",
        "limited purpose checking",
        "savings",
        "cash management"
      ],
      "limited_purpose_types": ["RENT_MORTGAGE"]
    }
  }
`}</code></pre>
    <p>{`To test this flow in Sandbox, use the institution "First Platypus Limited Purpose" with the username and password `}<inlineCode parentName="p">{`user_limited_purpose_checking`}</inlineCode>{` / `}<inlineCode parentName="p">{`pass_good`}</inlineCode>{`.`}</p>
    <p>{`Both `}<inlineCode parentName="p">{`current_balance`}</inlineCode>{` and `}<inlineCode parentName="p">{`available_balance`}</inlineCode>{` will be `}<inlineCode parentName="p">{`null`}</inlineCode>{` for limited-purpose checking accounts. If you opt in to enabling these accounts, ensure your balance check process and/or any Signal Rules in the Dashboard are set up to allow `}<inlineCode parentName="p">{`null`}</inlineCode>{` balances, and note that there may be additional risk of returned payments for these account types due to the inability to confirm funds in the account and the limited use cases of the accounts. To help manage this risk, it is recommended that you have `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/same-day-link-best-practices/"
      }}>{`anti-fraud measures`}</a>{` in place. If you require a list of Plaid-supported institutions that offer limited-purpose checking accounts, contact your account manager.  `}</p>
    <p>{`For Items with limited-purpose checking account types, if consent for Auth was not obtained on initial Link by adding `}<inlineCode parentName="p">{`auth`}</inlineCode>{` to the `}<inlineCode parentName="p">{`products`}</inlineCode>{`, `}<inlineCode parentName="p">{`optional_products`}</inlineCode>{`, `}<inlineCode parentName="p">{`required_if_supported_products`}</inlineCode>{`, or `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{` array, then the Item must go through `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`Update Mode`}</a>{` to add `}<inlineCode parentName="p">{`auth`}</inlineCode>{` `}<a parentName="p" {...{
        "href": "/docs/link/initializing-products/"
      }}>{`post-Link`}</a>{`.`}</p>
    <h4 {...{
      "id": "united-states-ach-auth-data"
    }}>{`United States (ACH) Auth data`}</h4>
    <p>{`The United States uses the Automated Clearing House (ACH) system for funds transfers, which uses account and routing numbers. Banks also have a second routing number, known as the wire transfer routing number. Wire transfers can be used to receive international payments and can be faster than ACH transfers, but often involve a fee. Plaid Auth can only provide wire transfer routing numbers for institutions in the US. `}</p>
    <p>{`For a detailed, comprehensive guide to ACH transfers and payments, see Plaid's `}<a parentName="p" {...{
        "href": "https://plaid.com/solutions/ACH-guide/"
      }}>{`Modern Guide to ACH`}</a>{`.`}</p>
    <APIVersion version='2017-03-08' mdxType="APIVersion">
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "#Example Auth data for US bank account",
          "#Example": true,
          "Auth": true,
          "data": true,
          "for": true,
          "US": true,
          "bank": true,
          "account": true
        }}>{`"numbers": [{
  "account": "9900009606",
  "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
  "routing": "011401533",
  "wire_routing": "021000021"
}]
`}</code></pre>
    </APIVersion>
    <APIVersion version='>2017-03-08' mdxType="APIVersion">
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "#Example Auth data for US bank account",
          "#Example": true,
          "Auth": true,
          "data": true,
          "for": true,
          "US": true,
          "bank": true,
          "account": true
        }}>{`"numbers": {
  "ach": [
    {
      "account": "1111222233330000",
      "account_id": "bWG9l1DNpdc8zDk3wBm9iMkW3p1mVWCVKVmJ6",
      "routing": "011401533",
      "wire_routing": "021000021"
    }
  ],
  "bacs": [],
  "eft": [],
  "international": []
}
`}</code></pre>
    </APIVersion>
    <h4 {...{
      "id": "auth-data-for-other-countries"
    }}>{`Auth data for other countries`}</h4>
    <h5 {...{
      "id": "canada-eft"
    }}>{`Canada (EFT)`}</h5>
    <p>{`In Canada, the routing number is in a different format than US routing numbers and broken into two pieces: the institution number, followed by the transit number (also known as the branch number). Canada uses Electronic Funds Transfer (EFT); `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships/vopay/"
      }}>{`VoPay`}</a>{` is a Plaid partner that can be used to process EFT transfers.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Example Auth data for Canadian bank account",
        "#Example": true,
        "Auth": true,
        "data": true,
        "for": true,
        "Canadian": true,
        "bank": true,
        "account": true
      }}>{`"numbers": {
  "eft": [
    {
      "account": "111122220000",
      "account_id": "qVZ3Bwbo5wFmoVneZxMksBvN6vDad6idkndAB",
      "branch": "01533",
      "institution": "114"
    }
  ],
  ...
}
`}</code></pre>
    <h5 {...{
      "id": "europe-sepa-transfers"
    }}>{`Europe (SEPA transfers)`}</h5>
    <Callout mdxType="Callout">
      <p>{`For funds transfers in Europe, also consider the `}<a parentName="p" {...{
          "href": "/docs/payment-initiation"
        }}>{`Payment Initiation API`}</a>{`, which allows end-to-end payments directly, without having to integrate an additional payment processor.`}</p>
    </Callout>
    <p>{`Across Europe, institutions use a Business Identifier Code (BIC), also known as a SWIFT code. Each bank account has an International Bank Account Number (IBAN), which is used along with the BIC for funds transfers. Many bank accounts also have internal, non-IBAN account numbers that cannot be used for funds transfers. The funds transfer system is known as the Single Euro Payments Area (SEPA), and it supports SEPA credit transfer, SEPA instant credit transfer, and SEPA direct debit. SEPA payments are always denominated in euros, so institutions in non-euro countries (such as the UK or Switzerland) participate in SEPA for euro-denominated payments only; their domestic-currency payments use other systems.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Example Auth data for European bank account",
        "#Example": true,
        "Auth": true,
        "data": true,
        "for": true,
        "European": true,
        "bank": true,
        "account": true
      }}>{`"numbers": {
  "international": [
    {
      "account_id": "blgvvBlXw3cq5GMPwqB6s6q4dLKB9WcVqGDGo",
      "bic": "BOFIIE2DXXX",
      "iban": "IE17BOFI90583812345678"
    }
    ...
  ]
}
`}</code></pre>
    <h5 {...{
      "id": "united-kingdom-bacs"
    }}>{`United Kingdom (Bacs)`}</h5>
    <Callout mdxType="Callout">
      <p>{`For funds transfers in the UK, also consider the `}<a parentName="p" {...{
          "href": "/docs/payment-initiation"
        }}>{`Payment Initiation API`}</a>{`, which allows end-to-end payments directly, without having to integrate an additional payment processor.`}</p>
    </Callout>
    <p>{`The UK uses the SEPA system as well as Bacs. Payments within the Bacs system cannot be made outside the UK and take several days to process. Bacs payments are typically used for recurring payments, such as direct debits for bills and subscriptions and direct credits for payroll. UK bank accounts will typically have both a Bacs sort code and an IBAN and support both Bacs transfers and SEPA transfers.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Example Auth data for UK bank account",
        "#Example": true,
        "Auth": true,
        "data": true,
        "for": true,
        "UK": true,
        "bank": true,
        "account": true
      }}>{`"numbers": {
  "bacs": [
    {
      "account": "80000000",
      "account_id": "blgvvBlXw3cq5GMPwqB6s6q4dLKB9WcVqGDGo",
      "sort_code": "040004"
    }
  ],
  "international": [
    {
      "account_id": "blgvvBlXw3cq5GMPwqB6s6q4dLKB9WcVqGDGo",
      "bic": "MONZGB21XXX",
      "iban": "GB23MONZ04000480000000"
    }
  ]
  ...
}
`}</code></pre>
    <h4 {...{
      "id": "sample-app-code"
    }}>{`Sample app code`}</h4>
    <p>{`For a real-life example of an app that incorporates Auth, see the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding"
      }}>{`Plaid Pattern Account Funding`}</a>{` sample app. Pattern Account Funding is a sample account funding app that fetches Auth data in order to set up funds transfers. The Auth code can be found in `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding/blob/master/server/routes/items.js#L133-L195"
      }}>{`items.js`}</a>{`.`}</p>
    <h4 {...{
      "id": "testing-auth"
    }}>{`Testing Auth`}</h4>
    <p>{`To test configuration options beyond those offered by the default Sandbox user, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/#configuring-the-custom-user-account"
      }}>{`Configuring the custom user account`}</a>{`.`}</p>
    <p>{`For details on testing Auth with more complex Auth flows such as micro-deposit-based Auth, first familiarize yourself with `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/"
      }}>{`Adding Institution Coverage`}</a>{`, then see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/testing/"
      }}>{`Test in Sandbox`}</a>{`.`}</p>
    <h4 {...{
      "id": "auth-pricing"
    }}>{`Auth pricing`}</h4>
    <p>{`Auth is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#one-time-fee"
      }}>{`one-time fee model`}</a>{`. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`Now that you understand Auth, `}<a parentName="p" {...{
        "href": "/docs/auth/add-to-app"
      }}>{`add Auth to your app`}</a>{`, or see `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships"
      }}>{`Move Money with Auth partners`}</a>{` to see specific instructions for configuring Auth with Plaid partners.`}</p>
    <p>{`If you're ready to launch to Production, see the Launch Center.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle="See next steps to launch in Production" link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "integration-process",
        level: 4,
        title: "Integration process",
      },
{
        id: "using-a-payments-service",
        level: 4,
        title: "Using a payments service",
      },
{
        id: "enabling-additional-verification-methods-with-the-account-verification-dashboard",
        level: 4,
        title: "Enabling additional verification methods with the Account Verification Dashboard",
      },
{
        id: "optimizing-the-link-ui-for-auth",
        level: 4,
        title: "Optimizing the Link UI for Auth",
      },
{
        id: "pay-by-bank-optimization",
        level: 4,
        title: "Pay by bank optimization",
      },
{
        id: "tokenized-account-numbers",
        level: 4,
        title: "Tokenized account numbers",
      },
{
        id: "updates-to-auth-data",
        level: 4,
        title: "Updates to Auth data",
      },
{
        id: "enabling-limited-purpose-checking-accounts-for-rent-or-mortgage",
        level: 4,
        title: "Enabling limited purpose checking accounts for rent or mortgage",
      },
{
        id: "united-states-ach-auth-data",
        level: 4,
        title: "United States (ACH) Auth data",
      },
{
        id: "auth-data-for-other-countries",
        level: 4,
        title: "Auth data for other countries",
      },
{
        id: "sample-app-code",
        level: 4,
        title: "Sample app code",
      },
{
        id: "testing-auth",
        level: 4,
        title: "Testing Auth",
      },
{
        id: "auth-pricing",
        level: 4,
        title: "Auth pricing",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
